import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, Divider, HStack, Radio, RadioGroup, Stack, Text, VStack } from "@chakra-ui/react";
import { AiFillWarning } from "react-icons/ai";
import {
  add,
  format,
  isPast,
  isValid,
  parse,
  roundToNearestMinutes,
  set
} from "date-fns";

import { useUser } from "context/user";
import { useQueryClient, QueryClient, UseMutationResult } from "@tanstack/react-query";
import { Session } from "types";
import { User, UserRole } from "types/user";
import { fetchAdminUsers, fetchUsers } from "api/webservice/users";
import { Form, Input } from "components";
import { formatDate } from "react-icalendar-link/dist/utils";
import DropdownMultiSearch, { UserOption } from "components/DropdownMultipleSearch";
import { useModal } from "context/modal";
import { useToast } from "hooks";


const roundAndFormat = (date: Date, isTime?: boolean): string => {
  const rounded = roundToNearestMinutes(date, { nearestTo: 15 });
  return format(rounded, isTime ? "HH:mm" : "yyyy-MM-dd");
};

const resetDate = (date: Date, isTime?: boolean): Date => {
  date.setMinutes(0);
  return date;
};

interface Props {
  session?: Session;
  mutationFn: () => UseMutationResult<any, unknown, any, unknown>;
  completed?: boolean;
  isUpdate?: boolean;
}


const SessionForm: React.FC<Props> = ({
  session,
  mutationFn,
  completed,
  isUpdate
}: Props) => {
  const { user: actor } = useUser()!;
  const toast = useToast();
  const {
    handleSubmit,
    errors,
    register,
    formState,
    setValue,
    getValues
  } = useForm<Partial<Session>>();

  const { closeModal } = useModal()!;

  const mutation = mutationFn();

  let initformtype;
  if (session && session.user_id === session.coach_id) initformtype = "Org";
  else initformtype = "Individual";

  const [formType, setFormType] = useState(initformtype || "Individual");
  const [selectedCoaches, setSelectedCoaches] = useState<readonly UserOption[]>([]);
  const [coaches, setCoaches] = useState<User[]>([]);
  const [clients, setClients] = useState<User[]>([]);
  const [date, setDate] = useState<Date>(
    session ? new Date(session.start_time) : new Date()
  );
  const [start, setStart] = useState<Date>(
    session ? new Date(session.start_time) : new Date()
  );
  const [end, setEnd] = useState<Date>(
    session ? new Date(session.end_time) : add(start, { hours: 1 })
  );
  const [warning, setWarning] = useState(false);

  window.onkeydown = function(event) {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      const dateElement = document.activeElement as HTMLInputElement;
      if (dateElement?.id == "start_time") {
        event.preventDefault();

        const newStart = resetDate(start, true);
        setStart(newStart);
        setEnd(add(newStart, { hours: 1 }));
        dateElement.value = roundAndFormat(newStart, true);
      }
      if (dateElement?.id == "end_time") {
        event.preventDefault();

        let newEnd = parse(dateElement.value, "HH:mm", end);;
        newEnd = resetDate(newEnd);
        setEnd(newEnd);
        dateElement.value = roundAndFormat(newEnd, true);
      }
      if (dateElement?.id == "date") {
        event.preventDefault();
      }
    }
  }

  useEffect(() => {
    fetchAdminUsers().then(response => {
      setCoaches(response);
      setValue("coach_id", session ? session.coach_id : actor.id);
    });
  }, [setCoaches]);

  useEffect(() => {
    fetchUsers().then(response => {
      setClients(response);
      if (session) setValue("user_id", session.user_id);
    });
  }, [setClients]);

  useEffect(() => {
    if (coaches && coaches.length > 0 && selectedCoaches.length == 0) {
      const coach: UserOption | null = getDefaultCoach();
      if (session && session.participants.length > 0) {
        const requiredCoach = session.coach_id || actor.id

        const participantOptions: UserOption[] = session.participants.map((x) =>
          x.first_name ?
            { label: `${x.first_name} ${x.last_name}`, value: x.id, user: x } :
            { label: x.username, value: x.id, user: x });

        const defaultCoach: UserOption = participantOptions.filter((x) => requiredCoach === x.user.id)[0];
        if (defaultCoach) {
          defaultCoach.main = true;
          defaultCoach.isFixed = true;
        }
        setSelectedCoaches([defaultCoach].concat(participantOptions.filter((x) => requiredCoach != x.user.id)));
      }
      else if (coach) {
        setSelectedCoaches([coach]);
      }
    }
  }, [coaches, selectedCoaches]); // WARNING we're assuming coaches is set exactly once. If it's not, this is bad.

  useEffect(() => {
    const currentDate = new Date();
    const diff = (start.getTime() - currentDate.getTime())/60000;
    if (diff <= 25) {
      setWarning(true);
    } else {
      setWarning(false);
    }
  }, [start]);

  const coachOptions: UserOption[] = coaches.map((x) =>
    x.first_name ?
      { label: `${x.first_name} ${x.last_name}`, value: x.id, user: x } :
      { label: x.username, value: x.id, user: x });

  const handleFormTypeUpdate = () => {
    if (formType === "Individual") {
      setFormType("Org");
    }
    else {
      setFormType("Individual");
    }
  };

  const getParticipantsArray = (user_id: string): User[] => {
    return clients.filter((x) => { return x.id === user_id; });
  };

  const getDefaultCoach = (): UserOption | null => {
    const defaultCoachId = session?.coach_id || actor.id
    const defaultCoach: UserOption = coachOptions.filter((x) => defaultCoachId === x.user.id)[0];
    if (defaultCoach) {
      defaultCoach.main = true;
      defaultCoach.isFixed = true;
      return defaultCoach;
    }
    return null;
  };

  const handleDateUpdate: React.ChangeEventHandler<HTMLInputElement> = e => {
    const newDate = parse(e.target.value, "yyyy-MM-dd", new Date());
    setDate(newDate);
    setStart(
      set(newDate, {
        hours: start.getHours(),
        minutes: start.getMinutes(),
        seconds: 0
      })
    );
    setEnd(
      set(newDate, {
        hours: end.getHours(),
        minutes: end.getMinutes(),
        seconds: 0
      })
    );
  };

  const handleStartTimeUpdate: React.ChangeEventHandler<HTMLInputElement> = e => {
    const newStart = parse(e.target.value, "HH:mm", start);
    setStart(newStart);
    setEnd(add(newStart, { hours: 1 }));
  };

  const handleEndTimeUpdate: React.ChangeEventHandler<HTMLInputElement> = e => {
    const newEnd = parse(e.target.value, "HH:mm", end);
    setEnd(newEnd);
  };

  const validateStart_time = (value: Date) => {
    if (isPast(new Date(value))) {
      return "Start Time has already passed";
    }
    return true;
  };

  const validateEnd_time = (value: Date) => {
    if (value <= getValues("start_time")!) {
      return "End Time must be later than Start Time";
    }
    return true;
  };

  const validateInstance_count = (value: number) => {
    if (value > 3) {
      return "You are limited to 3 instances";
    }
    return true;
  };

  const submitForm = async (data: Partial<Session>): Promise<any | null> => {
    return new Promise((resolve) => {
      const roundedStart = roundToNearestMinutes(start, { nearestTo: 15 });
      const roundedEnd = roundToNearestMinutes(end, { nearestTo: 15 });

      let participantsArray: User[];
      let coachID: string;
      let userID: string;
      if (formType === "Individual") {
        participantsArray = getParticipantsArray(data.user_id!);
        coachID = data.coach_id!;
        userID = data.user_id!;
      }
      else {
        participantsArray = selectedCoaches.map((x) => x.user);
        coachID = selectedCoaches.filter((x) => x.main)[0]?.user?.id;
        userID = coachID;
      }

      const onSettled = () => {
        closeModal();
        resolve(null);
      }

      const onError = (err: any, session: Session, context: any) => {
        console.error("error response", err.response);
        let errorMessage;
        errorMessage = "We're experiencing technical difficulties."
        if (err.response?.status < 500 && err.response?.data?.response) {
          errorMessage = err.response?.data?.response;
        }
        toast({
          title: "Sorry, we can't save your session.",
          description: errorMessage,
          status: "error"
        });
        resolve(err.response)
      }

      mutation.mutate({
        id: session?.id,
        ...data,
        start_time: set(date, {
          hours: roundedStart.getHours(),
          minutes: roundedStart.getMinutes()
        }),
        end_time: set(date, {
          hours: roundedEnd.getHours(),
          minutes: roundedEnd.getMinutes()
        }),
        participants: participantsArray,
        coach_id: coachID,
        user_id: userID
      }, { onSettled, onError });
    })
  };

  return (
    <Form onSubmit={handleSubmit(submitForm)}>
      <VStack spacing={4}>

        {(actor.privs >= UserRole.OrgAdmin)
          &&
          <RadioGroup
            defaultValue={formType}
            isDisabled={isUpdate}
          >
            <Stack spacing={5} direction='row'>
              <Radio colorScheme='blue' value='Individual' onChange={handleFormTypeUpdate}>
                Individual
              </Radio>
              <Radio colorScheme='blue' value='Org' onChange={handleFormTypeUpdate}>
                Organization
              </Radio>
            </Stack>
          </RadioGroup>
        }


        {formType === "Org"
          &&
          <DropdownMultiSearch
            size='md'
            name="participants"
            label="Participants"
            placeholder="Select up to 10 coaches"
            defaultValue={getDefaultCoach()}
            isClearable={coachOptions.some((v) => !v.isFixed)}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            options={coachOptions.filter((x) => !x.isFixed)}
            isMulti={true}
            onChange={(newValue, meta) => setSelectedCoaches((newValue as UserOption[]))}
            value={selectedCoaches}
          />
        }

        {formType === "Individual"
          &&
          <Input
            defaultValue={session?.coach_id || actor.id}
            size="md"
            variant="filled"
            type="select"
            name="coach_id"
            label="Coach"
            register={register()}
            required
            isDisabled={completed || actor.privs < UserRole.OrgAdmin}
            errorMessage={errors.coach_id?.message}
          >
            {coaches &&
              coaches.map(({ id, username, first_name, last_name }: User) => (
                <option value={id} key={id}>
                  {first_name ? `${first_name} ${last_name}` : username}
                </option>
              ))}
          </Input>
        }

        {formType === "Individual"
          &&
          <Input
            size="md"
            variant="filled"
            placeholder="Choose Client"
            type="select"
            name="user_id"
            label="Client"
            register={register()}
            required
            isDisabled={!!session}
            errorMessage={errors.user_id?.message}
          >
            {clients &&
              clients.map(({ id, username, first_name, last_name }: User) => (
                <option value={id} key={id}>
                  {first_name ? `${first_name} ${last_name}` : username}
                </option>
              ))}
          </Input>
        }



        <Input
          size="md"
          name="title"
          label="Title"
          defaultValue={session?.title}
          required
          register={register()}
          errorMessage={errors.title?.message}
        />

        <Input
          size="md"
          name="date"
          id="date"
          type="date"
          label="Date"
          value={roundAndFormat(date)}
          required
          register={register()}
          min={roundAndFormat(new Date())}
          onChangeOverride={handleDateUpdate}
          isDisabled={completed}
        />

        <Input
          value={roundAndFormat(start, true)}
          size="md"
          type="time"
          name="start_time"
          id="start_time"
          label="Start Time"
          required
          step={900}
          register={register({ validate: validateStart_time })}
          onChangeOverride={handleStartTimeUpdate}
          errorMessage={errors.start_time?.message}
          isDisabled={completed}
        //onKeyDown={disableBackspace}
        />

        <Input
          value={roundAndFormat(end, true)}
          size="md"
          type="time"
          name="end_time"
          id="end_time"
          label="End Time"
          required
          step={900}
          register={register({ validate: validateEnd_time })}
          onChangeOverride={handleEndTimeUpdate}
          errorMessage={errors.end_time?.message}
          isDisabled={completed}
        />

        <Input
          size="md"
          type="number"
          name="instance_count"
          label="Number of Instances"
          defaultValue={session?.instance_count ?? 1}
          required
          min={0}
          max={3}
          register={register({ validate: validateInstance_count })}
          errorMessage={errors.instance_count?.message}
          isDisabled={completed}
        />

        <Input
          size="md"
          type="checkbox"
          name="use_server"
          label="Use Server?"
          defaultChecked={session?.use_server ?? true}
          register={register()}
          errorMessage={errors.use_server?.message}
          isDisabled={completed}
        />
        {actor.privs >= UserRole.Admin && (
          <Input
            size="md"
            type="checkbox"
            name="billable"
            label="Billable?"
            defaultChecked={session?.billable ?? true}
            register={register()}
            errorMessage={errors.use_server?.message}
          />
        )}
        {warning && (
          <Box width="100%" borderRadius="8px" borderColor="#FDE68A" borderWidth="1px" bgColor="#FFFBEB" p="8px">
            <HStack alignItems="center" justifyContent="center">
              <AiFillWarning color="gold" size="20px"/>
              <Text color="#92400E" fontSize="14px" fontWeight="500" width="90%">
                Scheduling under 25 minutes before start time may cause technical issues.
              </Text>
            </HStack>
          </Box>
        )}
        <Divider maxW="150px" borderWidth="1px" rounded="full" />
        <Button
          disabled={formState.isSubmitting}
          type="submit"
          width="full"
          size="md"
        >
          Save Session
        </Button>
      </VStack>
    </Form>
  );
};

export default SessionForm;
